import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";

import { useError } from "../../../contexts/ErrorContext";
import { useExportStats } from "../../../hooks/useFulfillmentsAnalytics";

import {
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

interface ExportsStatsChartProps {
  dateStart?: string;
  dateEnd?: string;
}

const ExportsStatsChart: React.FC<ExportsStatsChartProps> = ({
  dateStart = undefined,
  dateEnd = undefined,
}) => {
  const { showError } = useError();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const [chartView, setChartView] = useState<string | null>("max_volume");

  const {
    data: exportStatsData,
    error,
    isLoading,
  } = useExportStats(dateStart, dateEnd);

  const handleChartViewToggle = (
    event: React.MouseEvent<HTMLElement>,
    newView: "max_volume" | "total_volume" | null
  ) => {
    if (newView !== null) {
      setChartView(newView);
    }
  };

  useEffect(() => {
    if (error) {
      showError(error?.message || `Failed to get export stats`);
    }
  }, [error, showError]);

  return (
    <Box
      sx={{
        display: "block",
        width: "100%",
        minHeight: "500px",
      }}
    >
      {isLoading ? (
        <CircularProgress
          size={20}
          sx={{ marginRight: 1, verticalAlign: "middle" }}
        />
      ) : exportStatsData && exportStatsData.length > 0 ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mb: 3,
            }}
          >
            <ToggleButtonGroup
              color="primary"
              value={chartView}
              exclusive
              onChange={handleChartViewToggle}
              aria-label="volume-chart-view"
            >
              <ToggleButton
                value="max_volume"
                aria-label="max_volume"
                style={{ width: "150px" }}
              >
                <Typography variant="button">Max Volume</Typography>
              </ToggleButton>
              <ToggleButton
                value="total_volume"
                aria-label="total_volume"
                style={{ width: "150px" }}
              >
                <Typography variant="button">Total Volume</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Plot
            config={{
              displaylogo: false,
              modeBarButtonsToRemove: ["select2d", "lasso2d"],
              responsive: true,
            }}
            data={[
              {
                x: exportStatsData.map((item) => {
                  const date = new Date(item.week_start_date);
                  return date.toISOString().split("T")[0];
                }),
                y: exportStatsData.map((item) =>
                  chartView === "max_volume"
                    ? item.max_row_count
                    : item.total_row_count
                ),
                type: "bar",
                name:
                  chartView === "max_volume" ? "Max Volume" : "Total Volume",
                marker: { color: "rgba(244, 162, 97, 0.5)" },
                yaxis: "y2",
                hovertemplate: `<b>${
                  chartView === "max_volume" ? "Max Volume" : "Total Volume"
                }:</b> %{y:,} rows<extra></extra>`,
              },
              {
                x: exportStatsData.map((item) => {
                  const date = new Date(item.week_start_date);
                  return date.toISOString().split("T")[0];
                }),
                y: exportStatsData.map((item) => item.min_duration_minutes),
                type: "scatter",
                mode: "lines+markers",
                name: "Min Duration (minutes)",
                hovertemplate: `<b>Min Duration:</b> %{y:,} mins<extra></extra>`,
                marker: { color: "#2A9D8F", size: 4 },
                line: { color: "#2A9D8F", width: 2 },
              },
              {
                x: exportStatsData.map((item) => {
                  const date = new Date(item.week_start_date);
                  return date.toISOString().split("T")[0];
                }),
                y: exportStatsData.map((item) => item.avg_duration_minutes),
                type: "scatter",
                mode: "lines+markers",
                name: "Avg Duration (minutes)",
                hovertemplate: `<b>Avg Duration:</b> %{y:,} mins<extra></extra>`,
                marker: { color: "#264653", size: 4 },
                line: { color: "##264653", width: 2 },
              },
              {
                x: exportStatsData.map((item) => {
                  const date = new Date(item.week_start_date);
                  return date.toISOString().split("T")[0];
                }),
                y: exportStatsData.map((item) => item.max_duration_minutes),
                type: "scatter",
                mode: "lines+markers",
                name: "Max Duration (minutes)",
                hovertemplate: `<b>Max Duration:</b> %{y:,} mins<extra></extra>`,
                marker: { color: "#E76F51", size: 4 },
                line: { color: "#E76F51", width: 2 },
              },
            ]}
            layout={{
              title: {
                text: "Shipping Export Duration Stats",
                font: {
                  color: primaryColor,
                },
              },
              xaxis: {
                title: {
                  text: "Week Start Date",
                  standoff: 15,
                },
                showgrid: false,
                tickangle: -45,
                tickfont: {
                  size: 12,
                },
              },
              yaxis: {
                title: "Duration (minutes)",
                showgrid: true,
                gridcolor: "#D3D3D3",
                rangemode: "tozero",
              },
              yaxis2: {
                title:
                  chartView === "max_volume" ? "Max Volume" : "Total Volume",
                overlaying: "y",
                side: "right",
                showgrid: false,
                rangemode: "tozero",
              },
              hovermode: "x unified",
              barmode: "relative",
              margin: {
                b: 100,
                t: 80,
                l: 60,
                r: 60,
              },
              hoverlabel: {
                bgcolor: "#FFF",
                font: { color: "#264653" },
                bordercolor: "#E76F51",
                align: "left",
              },
              font: {
                family: '"Noto Sans", serif',
                size: 14,
              },
              legend: {
                orientation: "h",
                x: 0.5,
                y: 1.15,
                xanchor: "center",
                yanchor: "top",
              },
            }}
            style={{ width: "100%", height: "100%" }}
          />
        </>
      ) : (
        <p>No data available</p>
      )}
    </Box>
  );
};

export default ExportsStatsChart;
